import { useEffect, useRef, useState } from "react";
import { Box, Grid, Typography, Link } from "@mui/material";
import partners from "../../../assets/partners.json";

function Partners() {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const node = sectionRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.2 }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <Box
            ref={sectionRef}
            className={isVisible ? "partners-section is-visible" : "partners-section"}
            sx={{
                position: "relative",
                width: "100vw",
                left: "50%",
                right: "50%",
                marginLeft: "-50vw",
                marginRight: "-50vw",
                background: "linear-gradient(135deg, #f5f7fa 0%, #edf1f7 100%)",
                py: { xs: 6, md: 10 },
                minHeight: { xs: "100vh", md: "calc(100vh - 70px)" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    maxWidth: "1400px",
                    margin: "0 auto",
                    px: { xs: 2.5, md: 5 },
            }}
            >
                <Box
                    sx={{
                        backgroundColor: "rgba(255,255,255,0.95)",
                        borderRadius: { xs: 3, md: 4 },
                        px: { xs: 3, sm: 4, md: 6 },
                        py: { xs: 4, sm: 5, md: 6 },
                    }}
                >
                    <Grid
                        container
                        spacing={{ xs: 4, md: 6 }}
                        alignItems="stretch"
                    >
                        {/* Left text block */}
                        <Grid item xs={12} md={5}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                pr: { md: 4 },
                            }}
                        >
                            <Typography
                                variant="overline"
                                sx={{
                                    letterSpacing: "0.18em",
                                    color: "rgba(15,23,42,0.6)",
                                    fontWeight: 600,
                                    mb: 1.5,
                                }}
                            >
                                Trusted network
                            </Typography>

                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 800,
                                    color: "var(--secondary-color)",
                                    fontSize: { xs: "2rem", sm: "2.3rem", md: "2.6rem" },
                                    lineHeight: 1.1,
                                    mb: 2,
                                }}
                            >
                                Our high‑level
                                <br />
                                partners
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{
                                    color: "rgba(15,23,42,0.7)",
                                    fontSize: { xs: "0.95rem", md: "1rem" },
                                    maxWidth: 360,
                                }}
                            >
                                We collaborate with leading organizations in additive
                                manufacturing to unlock reliable, scalable production
                                workflows.
                            </Typography>
                        </Grid>

                        {/* Right logo grid */}
                        <Grid item xs={12} md={7}>
                            <Grid
                                container
                                spacing={{ xs: 2.5, sm: 3 }}
                                alignItems="stretch"
                            >
                                {partners.map((partner) => (
                                    <Grid
                                        key={partner.id}
                                        item
                                        xs={6}
                                        sm={6}
                                        md={6}
                                    >
                                        <Link
                                            href={partner.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            sx={{
                                                display: "block",
                                                textDecoration: "none",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    backgroundColor: "#f5f5f7",
                                                    borderRadius: 3,
                                                    px: 2.25,
                                                    py: { xs: 2, sm: 2.5 },
                                                    minHeight: { xs: 96, sm: 110, md: 120 },
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    boxShadow:
                                                        "inset 0 0 0 1px rgba(148,163,184,0.25)",
                                                    transition:
                                                        "transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease",
                                                    "&:hover": {
                                                        transform: "translateY(-4px)",
                                                        boxShadow:
                                                            "0 18px 40px rgba(15, 23, 42, 0.15)",
                                                        backgroundColor: "#ffffff",
                                                    },
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={`${process.env.PUBLIC_URL}/static/images/Partners/${partner.logo}`}
                                                    alt={partner.title}
                                                    sx={{
                                                        maxWidth: { xs: 130, sm: 170 },
                                                        maxHeight: { xs: 48, sm: 56 },
                                                        width: "100%",
                                                        height: "auto",
                                                        objectFit: "contain",
                                                        filter: "grayscale(0.25)",
                                                    }}
                                                />
                                            </Box>
                                        </Link>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
}

export default Partners;